var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable quote-props */
import { createTheme, alpha } from '@mui/material';
import nunitoFaces from './font-families/nunito';
import merriweatherFaces from './font-families/merriweather';
import { getPaletteFromJson } from './importer';
import { elevationsDark, elevationsLight } from './elevations';
import ashworth from '../lib/tokens/ashworth.json';
import ics from '../lib/tokens/ics.json';
import jm from '../lib/tokens/james-madison.json';
import nyiad from '../lib/tokens/nyiad.json';
import nyicd from '../lib/tokens/nyicd.json';
import nyip from '../lib/tokens/nyip.json';
import pcdi from '../lib/tokens/pcdi.json';
import pf from '../lib/tokens/pf.json';
var brandTokens = {
    pennFoster: {
        label: 'Penn Foster',
        data: pf,
    },
    ashworth: {
        label: 'Ashworth',
        data: ashworth,
    },
    ics: {
        label: 'ICS',
        data: ics,
    },
    jamesMadison: {
        label: 'James Madison',
        data: jm,
    },
    nyiad: {
        label: 'NYIAD',
        data: nyiad,
    },
    nyicd: {
        label: 'NYICD',
        data: nyicd,
    },
    nyip: {
        label: 'NYIP',
        data: nyip,
    },
    pcdi: {
        label: 'PCDI',
        data: pcdi,
    },
};
export var DEFAULT_DRAWER_WIDTH = '480px';
var defaultTheme = createTheme();
export function makeTheme(palette, elevations) {
    return createTheme({
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1280,
                xl: 1400,
                tablet: 768,
            },
        },
        palette: __assign({}, palette),
        variables: {
            containerBorderRadius: 16,
        },
        typography: {
            fontFamily: ['Nunito Sans', 'Merriweather'].join(','),
            // Display Variants
            displayH1: {
                fontFamily: 'Nunito Sans',
                fontWeight: 300,
                fontSize: '96px',
                lineHeight: '112px',
            },
            displayH2: {
                fontFamily: 'Nunito Sans',
                fontWeight: 300,
                fontSize: '60px',
                lineHeight: '72px',
            },
            displayH3: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '48px',
                lineHeight: '56px',
            },
            // Heading Variants
            headingH4: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '34px',
                lineHeight: '48px',
            },
            headingH5: {
                fontFamily: 'Merriweather',
                fontWeight: 300,
                fontSize: '28px',
                lineHeight: '40px',
            },
            headingH6: {
                fontFamily: 'Merriweather',
                fontWeight: 400,
                fontSize: '24px',
                lineHeight: '36px',
            },
            desktopMessageH1: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '36px',
            },
            desktopMessageH2: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '18px',
                lineHeight: '36px',
            },
            desktopMessageH3: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '17px',
                lineHeight: '36px',
            },
            desktopMessageH4: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '17px',
                lineHeight: '36px',
            },
            desktopMessageH5: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '36px',
            },
            desktopMessageH6: {
                fontFamily: 'Nunito Sans',
                fontWeight: 300,
                fontSize: '16px',
                lineHeight: '36px',
            },
            // Subtitle Variants
            subtitle1: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '24px',
            },
            subtitle2: {
                fontFamily: 'Merriweather',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
            },
            // Label Variants
            labelLarge: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '15px',
                lineHeight: '24px',
                letterSpacing: '0.01em',
            },
            labelMedium: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.01em',
            },
            labelSmall: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.01em',
            },
            labelDisplay: {
                fontFamily: 'Nunito Sans',
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '32px',
            },
            overline: {
                fontFamily: 'Nunito Sans',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.01em',
                textTransform: 'uppercase',
            },
            // Body Variants
            body1: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
            },
            body2: {
                fontFamily: 'Nunito Sans',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
            },
            caption: {
                fontFamily: 'Nunito Sans',
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: 400,
            },
        },
        elevations: elevations,
        components: {
            MuiCssBaseline: {
                styleOverrides: "\n        ".concat(merriweatherFaces, "\n        ").concat(nunitoFaces, "\n        "),
            },
            MuiButton: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            borderRadius: '99px',
                            padding: '12px 24px',
                            textTransform: 'initial',
                            '&.Mui-disabled': {
                                backgroundColor: alpha(theme.palette.common.black, 0.1),
                            },
                        });
                    },
                    sizeSmall: function (_a) {
                        var theme = _a.theme;
                        return (__assign({}, theme.typography.labelSmall));
                    },
                    sizeMedium: function (_a) {
                        var theme = _a.theme;
                        return (__assign({}, theme.typography.labelMedium));
                    },
                    sizeLarge: function (_a) {
                        var theme = _a.theme;
                        return (__assign({}, theme.typography.labelLarge));
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: function (_a) {
                        var _b;
                        var theme = _a.theme;
                        return (_b = {
                                border: "1px solid ".concat(theme.palette.grey[200]),
                                borderRadius: "".concat(theme.variables.containerBorderRadius, "px")
                            },
                            _b[theme.breakpoints.down('sm')] = {
                                border: 'none',
                            },
                            _b);
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            border: "1px solid ".concat(theme.palette.grey[200]),
                            boxShadow: 'none',
                            margin: 0,
                            borderBottom: 'none',
                            '&:first-of-type': {
                                borderRadius: "".concat(theme.variables.containerBorderRadius, "px ").concat(theme.variables.containerBorderRadius, "px 0 0"),
                            },
                            '&:last-of-type': {
                                borderRadius: "0 0 ".concat(theme.variables.containerBorderRadius, "px ").concat(theme.variables.containerBorderRadius, "px"),
                                borderBottom: "1px solid ".concat(theme.palette.grey[200]),
                            },
                            '&:before': {
                                height: 0,
                            },
                            '&:last-of-type .MuiCollapse-wrapper': {
                                borderRadius: '0 0 24px 24px',
                                overflow: 'hidden',
                            },
                        });
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            color: theme.palette.common.white,
                        });
                    },
                    badge: {
                        lineHeight: '2em',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: "".concat(theme.variables.containerBorderRadius, "px"),
                            backgroundColor: theme.palette.common.white,
                            borderTopWidth: '4px',
                            borderTopColor: theme.palette.secondary.main,
                            borderTopStyle: 'solid',
                        });
                    },
                },
            },
            MuiCardHeader: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            padding: theme.spacing(3),
                            paddingBottom: theme.spacing(0),
                            flex: 1,
                        });
                    },
                    title: function (_a) {
                        var theme = _a.theme;
                        return (__assign({}, theme.typography.subtitle1));
                    },
                },
            },
            MuiCardContent: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            padding: theme.spacing(3),
                            flex: 1,
                        });
                    },
                },
            },
            MuiCardActions: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            padding: theme.spacing(3),
                            paddingTop: theme.spacing(0),
                            justifyContent: 'flex-end',
                        });
                    },
                },
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        padding: 0,
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: function (_a) {
                        var theme = _a.theme;
                        return ({
                            borderRadius: theme.spacing(2),
                            margin: 0,
                            padding: "".concat(theme.spacing(2.5), " ").concat(theme.spacing(4), " ").concat(theme.spacing(4), " ").concat(theme.spacing(4)),
                        });
                    },
                    container: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: alpha(theme.palette.neutral[20], 0.5),
                        });
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            paddingTop: theme.spacing(2),
                        });
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            paddingTop: theme.spacing(2),
                        });
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: function (_a) {
                        var theme = _a.theme;
                        return ({
                            padding: "".concat(theme.spacing(0), " ").concat(theme.spacing(2), " ").concat(theme.spacing(2)),
                        });
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: "".concat(theme.palette.common.white, " !important"),
                        });
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: theme.palette.common.white,
                        });
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    standardSuccess: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: theme.palette.success.container,
                            border: "1px solid ".concat(theme.palette.success.light),
                        });
                    },
                    standardError: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: theme.palette.error.container,
                            border: "1px solid ".concat(theme.palette.error.light),
                        });
                    },
                    standardWarning: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: theme.palette.warning.light,
                            border: "1px solid ".concat(theme.palette.warning.main),
                        });
                    },
                    standardInfo: function (_a) {
                        var theme = _a.theme;
                        return ({
                            backgroundColor: theme.palette.info.container,
                            border: "1px solid ".concat(theme.palette.info.light),
                        });
                    },
                },
            },
        },
    });
}
export function getTheme(tokens, isDarkTheme) {
    if (isDarkTheme === void 0) { isDarkTheme = false; }
    var palette = getPaletteFromJson(tokens, isDarkTheme);
    var elevation = isDarkTheme ? elevationsDark : elevationsLight;
    return makeTheme(palette, elevation);
}
export function makeBrandThemes() {
    var brandArray = Object.keys(brandTokens).map(function (key) {
        var _a = brandTokens[key], data = _a.data, label = _a.label;
        return {
            label: label,
            lightTheme: getTheme(data),
            darkTheme: getTheme(data, true),
        };
    });
    return brandArray;
}
export var brandThemes = makeBrandThemes();
export var pfLightTheme = getTheme(brandTokens.pennFoster.data);
export var pfDarkTheme = getTheme(brandTokens.pennFoster.data, true);
